// SicorDocumentCard - Layout reorganizado: Banco sozinho, 3 campos na segunda linha
import React, { useState } from 'react';
import { 
    FaUniversity, FaCalendarAlt, FaCreditCard, FaDollarSign, FaShieldAlt, 
    FaClipboardList, FaMapMarkerAlt, FaHandHoldingUsd, FaPercentage, 
    FaSeedling, FaMoneyBillWave, FaChartLine, FaIdCard, FaTint, FaChevronDown, FaChevronUp
} from 'react-icons/fa';

interface SicorDocumentCardProps {
    item: any;
    onClick?: () => void;
    className?: string;
}

interface CollapsibleSectionProps {
    title: string;
    fields: string[];
    item: any;
    getFieldName: (key: string) => string;
}

const CollapsibleSection: React.FC<CollapsibleSectionProps> = ({ title, fields, item, getFieldName }) => {
    const [isOpen, setIsOpen] = useState(false);

    return (
        <div>
            <button
                type="button"
                className="w-full flex items-center justify-between py-2 text-sm text-gray-600 hover:text-gray-800 border-t border-gray-100"
                onClick={(e) => {
                    e.stopPropagation();
                    setIsOpen(!isOpen);
                }}
            >
                <span>{title}</span>
                {isOpen ? <FaChevronUp className="text-xs" /> : <FaChevronDown className="text-xs" />}
            </button>
            
            {isOpen && (
                <div className="pt-2 border-t border-gray-100 space-y-2">
                    {fields.map(field => (
                        item[field] && (
                            <div key={field} className="flex items-start space-x-2">
                                {getFieldIcon(field, item[field])}
                                <div className="flex-1 min-w-0">
                                    <p className="text-xs text-gray-500">{getFieldName(field)}</p>
                                    <p className="text-sm text-gray-900 truncate" title={String(item[field])}>
                                        {String(item[field])}
                                    </p>
                                </div>
                            </div>
                        )
                    ))}
                </div>
            )}
        </div>
    );
};

const getFieldIcon = (key: string, value: any) => {
    const lowerKey = key.toLowerCase();
    
    if (lowerKey.includes('bank') || lowerKey.includes('banco')) return <FaUniversity className="text-blue-500 text-sm" />;
    if (lowerKey.includes('date') || lowerKey.includes('data')) return <FaCalendarAlt className="text-green-500 text-sm" />;
    if (lowerKey.includes('value') || lowerKey.includes('valor') || lowerKey.includes('total')) return <FaDollarSign className="text-green-600 text-sm" />;
    if (lowerKey.includes('status') || lowerKey.includes('situacao')) return <FaShieldAlt className="text-yellow-500 text-sm" />;
    if (lowerKey.includes('year') || lowerKey.includes('ano')) return <FaCalendarAlt className="text-purple-500 text-sm" />;
    if (lowerKey.includes('document') || lowerKey.includes('documento')) return <FaIdCard className="text-orange-500 text-sm" />;
    if (lowerKey.includes('irrigation') || lowerKey.includes('irrigacao')) return <FaTint className="text-blue-400 text-sm" />;
    
    return <FaClipboardList className="text-gray-500 text-sm" />;
};

const formatFieldName = (key: string) => {
    const fieldNames: { [key: string]: string } = {
        'bank': 'Banco',
        'emissionDate': 'Data de Emissão',
        'totalValue': 'Valor Total',
        'status': 'Situação',
        'year': 'Ano',
        'documentFormatted': 'Documento'
    };
    
    return fieldNames[key] || key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());
};

const SicorDocumentCard: React.FC<SicorDocumentCardProps> = ({ item, onClick, className = '' }) => {
    // Debug temporário
    console.log('SicorDocumentCard - item:', item);
    console.log('SicorDocumentCard - item.bank:', item.bank);
    console.log('SicorDocumentCard - item.year:', item.year);
    console.log('SicorDocumentCard - item.documentFormatted:', item.documentFormatted);
    
    const mainFields = ['bank', 'emissionDate', 'totalValue', 'status'];
    const otherFields = Object.keys(item).filter(key => 
        !mainFields.includes(key) && 
        key !== 'bacenId' && 
        item[key] !== null && 
        item[key] !== undefined && 
        item[key] !== ''
    );

    const getFieldName = (key: string) => {
        const fieldNames: { [key: string]: string } = {
            'year': 'Ano',
            'documentFormatted': 'Documento'
        };
        return fieldNames[key] || formatFieldName(key);
    };

    return (
        <div 
            className={`bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow duration-200 p-4 cursor-pointer border border-gray-200 ${className}`}
            onClick={onClick}
        >
            {/* Header com campos principais reorganizados */}
            <div className="mb-3 pb-3 border-b border-gray-100">
                {/* Primeira linha: Banco sozinho */}
                {item.bank && (
                    <div className="mb-3">
                        <div className="flex items-start space-x-2">
                            {getFieldIcon('bank', item.bank)}
                            <div className="flex-1 min-w-0">
                                <p className="text-xs text-gray-500 font-medium">Banco</p>
                                <p className="text-sm font-semibold text-gray-900 truncate" title={item.bank}>
                                    {item.bank}
                                </p>
                            </div>
                        </div>
                    </div>
                )}

                {/* Segunda linha: Data de emissão, Valor total e Situação */}
                <div className="grid grid-cols-3 gap-3">
                    {item.emissionDate && (
                        <div className="flex items-start space-x-2">
                            {getFieldIcon('emissionDate', item.emissionDate)}
                            <div className="flex-1 min-w-0">
                                <p className="text-xs text-gray-500 font-medium">Data de Emissão</p>
                                <p className="text-sm font-semibold text-gray-900 truncate" title={item.emissionDate}>
                                    {item.emissionDate}
                                </p>
                            </div>
                        </div>
                    )}
                    {item.totalValue && (
                        <div className="flex items-start space-x-2">
                            {getFieldIcon('totalValue', item.totalValue)}
                            <div className="flex-1 min-w-0">
                                <p className="text-xs text-gray-500 font-medium">Valor Total</p>
                                <p className="text-sm font-semibold text-gray-900 truncate" title={item.totalValue}>
                                    {item.totalValue}
                                </p>
                            </div>
                        </div>
                    )}
                    {item.status && (
                        <div className="flex items-start space-x-2">
                            {getFieldIcon('status', item.status)}
                            <div className="flex-1 min-w-0">
                                <p className="text-xs text-gray-500 font-medium">Situação</p>
                                <p className="text-sm font-semibold text-gray-900 truncate" title={item.status}>
                                    {item.status}
                                </p>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Outros campos colapsáveis */}
            {otherFields.length > 0 && (
                <CollapsibleSection 
                    title={`+${otherFields.length} campos adicionais`}
                    fields={otherFields}
                    item={item}
                    getFieldName={getFieldName}
                />
            )}
        </div>
    );
};

export default SicorDocumentCard;
